import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { getTierInfo as getTierInfoFromConfig } from '../../../utils/tierConfig';
import { getPlanLimitsForTier } from '../../../utils/planLimits';

const CreditHistory = ({ userId }) => {
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const fetchHistory = async () => {
      try {
        // Only fetch current month's transactions (resets monthly)
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

        const { data: designs, error } = await supabase
          .from('saved_designs')
          .select('id, created_at, generation_metadata(model_used, style, additional_params)')
          .eq('user_id', userId)
          .gte('created_at', monthStart)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Credit history fetch error:', error);
          setIsLoading(false);
          return;
        }

        const txns = (designs || []).map((d) => {
          const meta = Array.isArray(d.generation_metadata) ? d.generation_metadata[0] : d.generation_metadata;
          const cost = meta?.additional_params?.creditCost ?? meta?.additional_params?.credit_cost ?? 1;
          return {
            id: d.id,
            type: 'spend',
            credits: -cost,
            feature: meta?.style || 'Generation',
            date: d.created_at,
          };
        });

        setTransactions(txns);
      } catch (err) {
        console.error('Credit history error:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchHistory();
  }, [userId]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const filtered = transactions.filter((t) => {
    const matchesType = filterType === 'all' || t.type === filterType;
    const matchesSearch = !searchQuery || t.feature?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  const typeBadge = (type) => {
    const styles = {
      spend: { bg: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', text: 'text-white', icon: 'TrendingDown' },
      refund: { bg: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)', text: 'text-green-400', icon: 'TrendingUp' },
      credit: { bg: 'rgba(254,131,56,0.1)', border: '1px solid rgba(254,131,56,0.25)', text: 'text-orange-400', icon: 'TrendingUp' },
    };
    const s = styles[type] || styles.spend;
    const label = type.charAt(0).toUpperCase() + type.slice(1);
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${s.text}`} style={{ background: s.bg, border: s.border }}>
        <Icon name={s.icon} size={12} />
        {label}
      </span>
    );
  };

  const filterOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'spend', label: 'Spend' },
    { value: 'refund', label: 'Refund' },
    { value: 'credit', label: 'Credit' },
  ];

  return (
    <div className="glassmorphic-card rounded-2xl p-6 md:p-8">
      <div className="flex items-center gap-2 mb-5">
        <Icon name="Clock" size={20} className="text-white/40" />
        <h3 className="text-lg font-bold text-white">Credit Usage History</h3>
      </div>

      {/* Search & Filter */}
      <div className="flex gap-3 mb-5">
        <div className="flex-1 relative">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            type="text"
            placeholder="Search transactions..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm text-white placeholder:text-white/30 focus:outline-none transition-all"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
            onFocus={(e) => { e.target.style.border = '1px solid rgba(254,131,56,0.4)'; }}
            onBlur={(e) => { e.target.style.border = '1px solid rgba(255,255,255,0.1)'; }}
          />
        </div>
        <div className="relative">
          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm text-white/70 transition-all hover:text-white"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <Icon name="Filter" size={14} />
            {filterOptions.find(o => o.value === filterType)?.label}
            <Icon name="ChevronDown" size={14} className="text-white/40" />
          </button>
          {isFilterOpen && (
            <div className="absolute right-0 top-full mt-1 z-50 w-36 rounded-xl overflow-hidden shadow-2xl" style={{ background: 'rgba(0,0,0,0.95)', border: '1px solid rgba(255,255,255,0.1)' }}>
              {filterOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => { setFilterType(opt.value); setIsFilterOpen(false); }}
                  className={`w-full text-left px-3 py-2 text-xs transition-all ${filterType === opt.value ? 'bg-orange-500/20 text-white' : 'text-white/60 hover:bg-white/5 hover:text-white'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
        {/* Header */}
        <div className="grid grid-cols-[1fr_0.7fr_1.5fr_0.8fr] md:grid-cols-[1fr_0.7fr_2fr_1fr] px-4 py-3 text-xs text-white/40 font-medium" style={{ background: 'rgba(255,255,255,0.02)' }}>
          <span>Type</span>
          <span>Credits</span>
          <span>Feature</span>
          <span className="text-right">Date</span>
        </div>

        {/* Scrollable rows — scrolls when more than 10 transactions */}
        <div className={filtered.length > 10 ? 'max-h-[440px] overflow-y-auto' : ''}>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Icon name="Loader" size={24} className="text-orange-400 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Icon name="FileText" size={32} className="text-white/20 mb-2" />
              <p className="text-sm text-white/40">No transactions this month</p>
            </div>
          ) : (
            filtered.map((txn, idx) => (
              <div
                key={txn.id || idx}
                className="grid grid-cols-[1fr_0.7fr_1.5fr_0.8fr] md:grid-cols-[1fr_0.7fr_2fr_1fr] px-4 py-3 items-center transition-colors hover:bg-white/[0.02]"
                style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}
              >
                <div>{typeBadge(txn.type)}</div>
                <span className={`text-sm font-semibold ${txn.credits > 0 ? 'text-green-400' : 'text-white'}`}>
                  {txn.credits > 0 ? '+' : ''}{txn.credits}
                </span>
                <span className="text-sm text-white/60 truncate">{txn.feature}</span>
                <span className="text-xs text-white/40 text-right">{formatDate(txn.date)}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Footer count */}
      {filtered.length > 0 && (
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-white/30">{filtered.length} transaction{filtered.length !== 1 ? 's' : ''} this month</span>
          <span className="text-xs text-white/20">Resets monthly</span>
        </div>
      )}
    </div>
  );
};

const GlassButton = ({ children, variant = 'primary', icon, onClick, className = '' }) => {
  const base = 'inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 cursor-pointer';
  const variants = {
    primary: 'glassmorphic-button text-white hover:scale-[1.02]',
    outline: 'bg-white/[0.05] border border-white/10 text-white/70 hover:text-white hover:border-white/20 hover:bg-white/[0.08]',
    danger: 'border border-red-500/30 text-red-400 hover:bg-red-500/10',
  };
  return (
    <button onClick={onClick} className={`${base} ${variants[variant]} ${className}`}>
      {icon && <Icon name={icon} size={16} />}
      {children}
    </button>
  );
};

const SubscriptionSection = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [projectCount, setProjectCount] = useState(0);
  const [designCount, setDesignCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [maxProjects, setMaxProjects] = useState(null);
  const [maxStorage, setMaxStorage] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelStep, setCancelStep] = useState(1);
  const [isCancelling, setIsCancelling] = useState(false);

  const getTierInfo = (tier) => getTierInfoFromConfig(tier);

  const handleUpgrade = () => navigate('/subscription-payment');

  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
        }
      );

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to cancel');

      setShowCancelModal(false);
      setCancelStep(1);
      window.location.reload();
    } catch (err) {
      console.error('Cancel subscription error:', err);
    } finally {
      setIsCancelling(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Fetch real project and design counts + plan limits
  useEffect(() => {
    const fetchUsageStats = async () => {
      if (!user?.id) return;
      try {
        const tier = userProfile?.subscription_tier || 'free';
        const isTeamOwner = tier === 'team';

        let projCount, saveCount, uploadCount = 0;
        if (isTeamOwner) {
          // Team owner: count team total (owner + all members)
          const [projRes, saveRes, uploadRes] = await Promise.all([
            supabase.rpc('get_team_total_project_count'),
            supabase.rpc('get_team_total_save_design_count'),
            supabase.rpc('count_uploaded_media', { target_user_id: user.id }),
          ]);
          projCount = projRes.data ?? 0;
          saveCount = saveRes.data ?? 0;
          uploadCount = uploadRes.data ?? 0;
        } else {
          // Regular user or team member: count own
          const [projectsRes, designsRes, uploadRes] = await Promise.all([
            supabase.from('projects').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
            supabase.from('saved_designs').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
            supabase.rpc('count_uploaded_media', { target_user_id: user.id }),
          ]);
          projCount = projectsRes.count ?? 0;
          saveCount = designsRes.count ?? 0;
          uploadCount = uploadRes.data ?? 0;
        }

        const limits = await getPlanLimitsForTier(tier, user.id);
        setProjectCount(projCount);
        setDesignCount(saveCount + uploadCount);
        setMaxProjects(limits.maxProjects);
        setMaxStorage(limits.maxStorage);
      } catch (err) {
        console.error('Error fetching usage stats:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsageStats();
  }, [user?.id, userProfile?.subscription_tier]);

  const subscriptionTier = userProfile?.subscription_tier || 'free';
  const tierInfo = getTierInfo(subscriptionTier);

  // All values derived from real user data
  const remainingCredits = userProfile?.remaining_credits ?? 0;
  const monthlyCredits = userProfile?.monthly_credits ?? tierInfo.monthlyCredits;
  const accountCreated = formatDate(userProfile?.created_at);
  const planPrice = tierInfo.priceMonthly;

  return (
    <div className="space-y-6">
      {/* Current Subscription */}
      <div className="glassmorphic-card rounded-2xl p-6 md:p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-white">Current Subscription</h2>
            <p className="text-sm text-white/40 mt-1">Manage your subscription and billing</p>
          </div>
          <span className="px-3 py-1 rounded-full text-xs font-medium text-emerald-400" style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
            {subscriptionTier === 'team_member' ? 'Team' : subscriptionTier === 'free' ? 'Free' : 'Active'}
          </span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-6">
          <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center gap-2 mb-2">
              <Icon name="CreditCard" size={14} className="text-white/40" />
              <span className="text-xs text-white/40">Plan</span>
            </div>
            <p className={`text-xl font-bold ${tierInfo.color}`}>{tierInfo.label}</p>
          </div>
          <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Calendar" size={14} className="text-white/40" />
              <span className="text-xs text-white/40">Member Since</span>
            </div>
            <p className="text-xl font-bold text-white">{accountCreated}</p>
          </div>
          <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center gap-2 mb-2">
              <Icon name="DollarSign" size={14} className="text-white/40" />
              <span className="text-xs text-white/40">Billing Amount</span>
            </div>
            <p className="text-xl font-bold text-white">{subscriptionTier === 'team_member' ? 'Team' : planPrice > 0 ? `$${planPrice}/mo` : 'Free'}</p>
          </div>
          <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Zap" size={14} className="text-white/40" />
              <span className="text-xs text-white/40">Credits Remaining</span>
            </div>
            <p className="text-xl font-bold text-white">{remainingCredits} <span className="text-sm text-white/40 font-normal">/ {monthlyCredits}</span></p>
          </div>
          <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Layers" size={14} className="text-white/40" />
              <span className="text-xs text-white/40">Projects Created</span>
            </div>
            <p className="text-xl font-bold text-white">{isLoading ? '—' : projectCount} <span className="text-sm text-white/40 font-normal">/ {maxProjects === null ? '∞' : maxProjects}</span></p>
          </div>
          <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Image" size={14} className="text-white/40" />
              <span className="text-xs text-white/40">Storage</span>
            </div>
            <p className="text-xl font-bold text-white">{isLoading ? '—' : designCount} <span className="text-sm text-white/40 font-normal">/ {maxStorage === null ? '∞' : maxStorage}</span></p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          {subscriptionTier === 'team_member' ? (
            <p className="text-sm text-white/40">บัญชีนี้ถูกจัดการโดย Team Owner ไม่สามารถเปลี่ยนแผนได้</p>
          ) : (
          <>
          <button
            onClick={handleUpgrade}
            className="glassmorphic-button rounded-xl px-5 py-2.5 text-white text-sm font-medium flex items-center gap-2 hover:scale-[1.02] transition-transform"
          >
            <Icon name={subscriptionTier === 'free' ? 'ArrowUp' : 'RefreshCw'} size={16} />
            {subscriptionTier === 'free' ? 'Upgrade Plan' : 'Change Plan'}
          </button>
          {subscriptionTier !== 'free' && (
            <button
              onClick={() => { setCancelStep(1); setShowCancelModal(true); }}
              className="rounded-xl px-5 py-2.5 text-red-400 text-sm font-medium flex items-center gap-2 border border-red-500/20 bg-red-500/5 hover:bg-red-500/10 hover:scale-[1.02] transition-all"
            >
              <Icon name="XCircle" size={16} />
              Cancel Subscription
            </button>
          )}
          </>
          )}
        </div>
      </div>

      {/* Credit History */}
      <CreditHistory userId={user?.id} />

      {/* Invoice History */}
      <div className="glassmorphic-card rounded-2xl p-6 md:p-8">
        <h3 className="text-lg font-bold text-white mb-4">Invoice History</h3>
        <div className="rounded-xl py-10 flex flex-col items-center justify-center" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <Icon name="FileText" size={36} className="text-white/20 mb-2" />
          <p className="text-sm text-white/40">No invoices yet</p>
        </div>
      </div>

      {/* Cancel Subscription Modal — 2-step confirmation */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" style={{ backdropFilter: 'blur(8px)' }}>
          <div className="glassmorphic-card rounded-2xl max-w-md w-full p-6" style={{ border: '1px solid rgba(254, 131, 56, 0.15)' }}>

            {cancelStep === 1 && (
              <>
                <div className="flex items-start gap-3 mb-5">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(254, 131, 56, 0.1)', border: '1px solid rgba(254, 131, 56, 0.2)' }}>
                    <Icon name="AlertTriangle" size={22} className="text-orange-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">Cancel Subscription?</h3>
                    <p className="text-xs text-white/40">Are you sure you want to cancel your subscription?</p>
                  </div>
                </div>

                <div className="p-4 rounded-xl mb-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <p className="text-xs text-white/50 mb-2">If you cancel, you will:</p>
                  <ul className="text-xs text-white/40 space-y-1.5">
                    <li className="flex items-center gap-2"><Icon name="X" size={14} className="text-orange-400" />Lose access to premium features</li>
                    <li className="flex items-center gap-2"><Icon name="X" size={14} className="text-orange-400" />Be downgraded to the Free plan</li>
                    <li className="flex items-center gap-2"><Icon name="X" size={14} className="text-orange-400" />Keep your projects but with limited credits</li>
                  </ul>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setCancelStep(2)}
                    className="flex-1 py-2.5 rounded-xl text-sm font-medium text-red-400 border border-red-500/20 bg-red-500/5 hover:bg-red-500/10 transition-all flex items-center justify-center gap-2"
                  >
                    <Icon name="XCircle" size={16} />
                    Yes, Cancel
                  </button>
                  <button
                    onClick={() => setShowCancelModal(false)}
                    className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white/70 border border-white/10 hover:bg-white/5 transition-all"
                  >
                    Keep Plan
                  </button>
                </div>
              </>
            )}

            {cancelStep === 2 && (
              <>
                <div className="flex items-start gap-3 mb-5">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                    <Icon name="AlertOctagon" size={22} className="text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">This cannot be undone</h3>
                    <p className="text-xs text-white/40">Your plan will be downgraded to Free immediately. You will lose your remaining credits and premium access.</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleCancelSubscription}
                    disabled={isCancelling}
                    className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white bg-red-500/20 border border-red-500/30 hover:bg-red-500/30 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Icon name="XCircle" size={16} />
                    {isCancelling ? 'Cancelling...' : 'Confirm Cancellation'}
                  </button>
                  <button
                    onClick={() => { setShowCancelModal(false); setCancelStep(1); }}
                    className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white/70 border border-white/10 hover:bg-white/5 transition-all"
                  >
                    Go Back
                  </button>
                </div>
              </>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionSection;
